/**
 * Lab Safety Showdown - static question bank.
 *
 * Four kinds of prompts:
 *  - "scenario-hazard" : describe a lab scene; player picks the hazard/violation.
 *  - "scenario-action" : describe an incident; player picks the correct first action.
 *  - "ghs-pictogram"   : show a GHS hazard symbol; player picks what it means.
 *  - "ppe-id"          : describe a task; player picks the right PPE.
 *
 * Each item supplies its own four choices and a per-question explanation, so
 * the engine doesn't need to invent distractors or reasoning at runtime.
 */

export type SafetyQuestionKind =
  | "scenario-hazard"
  | "scenario-action"
  | "ghs-pictogram"
  | "ppe-id";

export interface SafetyQuestion {
  id: string;
  kind: SafetyQuestionKind;
  /** Headline shown above the choices. */
  prompt: string;
  /** Optional secondary line - scenario detail, equation, or instructions. */
  detail?: string;
  /**
   * GHS pictogram code (only set for "ghs-pictogram"). Rendered by the UI
   * using a small inline SVG so we don't ship raster assets.
   */
  pictogram?: GhsPictogram;
  /** PPE icon emoji (only set for "ppe-id"). */
  ppeIcon?: string;
  choices: [string, string, string, string];
  correctIndex: 0 | 1 | 2 | 3;
  /** Short, single-sentence explanation shown after the answer. */
  explanation: string;
  /** Longer pedagogical reasoning shown in the disclosure. */
  whyCorrect: string;
}

export type GhsPictogram =
  | "flame"
  | "exclamation"
  | "skull"
  | "corrosion"
  | "health-hazard"
  | "environment"
  | "exploding-bomb"
  | "flame-over-circle"
  | "gas-cylinder";

export const SAFETY_QUESTIONS: SafetyQuestion[] = [
  // ---------- scenario-hazard ----------
  {
    id: "sh-sealed-flask",
    kind: "scenario-hazard",
    prompt: "What's wrong with this setup?",
    detail: "Mia clamps a stoppered Erlenmeyer flask over a Bunsen burner and starts heating it.",
    choices: [
      "Heating a sealed container - pressure can shatter the glass",
      "Erlenmeyer flasks shouldn't be clamped",
      "Bunsen burners can't heat glass",
      "She should be using tongs, not a clamp",
    ],
    correctIndex: 0,
    explanation: "Never heat a closed system - trapped vapour expands and can explode.",
    whyCorrect:
      "When liquid is heated, vapour pressure rises rapidly. With no vent, the pressure has nowhere to go and the flask can shatter or rocket off the clamp. Always heat in an open vessel, or use a reflux condenser if vapour must be retained.",
  },
  {
    id: "sh-add-water-to-acid",
    kind: "scenario-hazard",
    prompt: "Spot the mistake.",
    detail: "Jordan slowly pours water into a beaker of concentrated sulfuric acid to dilute it.",
    choices: [
      "Pouring water into acid - should be acid into water",
      "Sulfuric acid can't be diluted",
      "He should use a graduated cylinder, not a beaker",
      "Dilution should always be done in a fume hood",
    ],
    correctIndex: 0,
    explanation: "Always Add Acid (AAA): add acid to water, never water to acid.",
    whyCorrect:
      "Dilution of strong acids is highly exothermic. Adding water to acid concentrates the heat in a small water layer that can flash-boil and spatter acid out of the beaker. Adding acid to a larger volume of water spreads the heat safely.",
  },
  {
    id: "sh-mouth-pipette",
    kind: "scenario-hazard",
    prompt: "What's the safety violation?",
    detail: "Sam uses their mouth to draw 0.1 M HCl into a glass pipette.",
    choices: [
      "Mouth pipetting - use a pipette bulb or pump",
      "0.1 M HCl is too dilute to need PPE",
      "Glass pipettes are banned in schools",
      "They should be wearing earplugs",
    ],
    correctIndex: 0,
    explanation: "Mouth pipetting is forbidden in every school and professional lab.",
    whyCorrect:
      "Even dilute acids and seemingly harmless solutions can cause serious oral, throat or lung injury if accidentally swallowed or inhaled. Always use a pipette bulb, pump, or auto-pipettor.",
  },
  {
    id: "sh-long-hair",
    kind: "scenario-hazard",
    prompt: "Identify the hazard.",
    detail: "Priya leans over a lit Bunsen burner. Her long hair is loose and her sleeves are rolled down.",
    choices: [
      "Loose hair and dangling sleeves near an open flame",
      "She's standing too close to the bench",
      "Bunsen burners must be lit by a match, not a striker",
      "She should be wearing a hard hat",
    ],
    correctIndex: 0,
    explanation: "Tie back long hair and secure loose clothing before using open flames.",
    whyCorrect:
      "Hair and fabric ignite almost instantly in a Bunsen flame, often without the wearer noticing. Tying hair back, rolling sleeves up, and removing scarves dramatically lowers the risk of clothing fires.",
  },
  {
    id: "sh-eat-in-lab",
    kind: "scenario-hazard",
    prompt: "Spot the violation.",
    detail: "Alex sips from a water bottle they brought into the lab while waiting for a reaction.",
    choices: [
      "Eating or drinking is forbidden in any working lab",
      "Water bottles must be glass, not plastic",
      "They should drink from a beaker instead",
      "Drinking near reactions is fine if PPE is on",
    ],
    correctIndex: 0,
    explanation: "No food, drink, gum, or cosmetics in the lab - contamination risk.",
    whyCorrect:
      "Airborne or splashed chemicals can settle on the lip of a bottle or the rim of a cup. Even a clean-looking bench can have invisible residues. Step into a designated non-lab area before eating or drinking.",
  },
  {
    id: "sh-broken-glass-hand",
    kind: "scenario-hazard",
    prompt: "What's the issue?",
    detail: "A test tube cracks. Lin reaches in with bare fingers to pick up the pieces.",
    choices: [
      "Picking up broken glass with bare hands",
      "Cracked test tubes can be reused after rinsing",
      "They should crush the pieces first",
      "Broken glass goes in the regular trash",
    ],
    correctIndex: 0,
    explanation: "Use a dustpan/brush and dispose in a labelled sharps/glass bin.",
    whyCorrect:
      "Lab glass shards are razor-sharp and may be coated in chemicals. Use a brush and dustpan or tongs, place pieces in a rigid sharps/glass-only container, and never the regular trash where someone could be cut later.",
  },
  {
    id: "sh-reagent-bottle-back",
    kind: "scenario-hazard",
    prompt: "What's wrong here?",
    detail: "After pouring out NaOH pellets, Devon pours the unused pellets back into the original bottle.",
    choices: [
      "Never return unused chemicals to the stock bottle",
      "NaOH must be stored in plastic bottles only",
      "Pellets should be weighed twice before storage",
      "Devon should use a funnel",
    ],
    correctIndex: 0,
    explanation: "Returned chemicals can contaminate the entire stock supply.",
    whyCorrect:
      "Anything that touched the bench, your spatula, or air moisture is now potentially contaminated. Returning it spoils the entire stock for every future user. Dispose of the excess in the appropriate waste container.",
  },
  {
    id: "sh-fume-hood-sash",
    kind: "scenario-hazard",
    prompt: "Spot the mistake.",
    detail: "Riley opens a bottle of concentrated ammonia on the open bench, away from any fume hood.",
    choices: [
      "Volatile/toxic vapours must be handled in a fume hood",
      "Ammonia bottles should never be opened indoors at all",
      "They should be wearing a respirator instead",
      "Ammonia is only a hazard above 50 °C",
    ],
    correctIndex: 0,
    explanation: "Concentrated ammonia releases choking, corrosive vapours - use a fume hood.",
    whyCorrect:
      "Ammonia gas irritates the eyes, throat and lungs at low concentrations. Open bottles draw air movement around the lab - a fume hood with the sash at the recommended working height pulls vapours safely away from the user.",
  },

  // ---------- scenario-action ----------
  {
    id: "sa-acid-spill-skin",
    kind: "scenario-action",
    prompt: "What do you do FIRST?",
    detail: "You spill dilute hydrochloric acid on your forearm.",
    choices: [
      "Rinse the area with cool running water for at least 15 minutes",
      "Neutralise it with sodium bicarbonate paste",
      "Cover the burn with a dry cloth and continue work",
      "Apply hand sanitiser to disinfect",
    ],
    correctIndex: 0,
    explanation: "Flood the skin with water - at least 15 minutes - then notify the teacher.",
    whyCorrect:
      "Copious water dilutes and removes the acid faster than any neutralising agent could safely react. Bicarbonate on skin generates extra heat. Always rinse first, then seek the teacher and the first-aid kit.",
  },
  {
    id: "sa-fire-clothing",
    kind: "scenario-action",
    prompt: "Your lab coat catches fire. What do you do?",
    choices: [
      "Stop, drop, and roll - or use the safety shower",
      "Run to the door for fresh air",
      "Blow on the flames",
      "Take off the coat and shake it out",
    ],
    correctIndex: 0,
    explanation: "Smother the flames - running fans them. Use the safety shower if nearby.",
    whyCorrect:
      "Flames need oxygen. Running pulls air across the fire, intensifying it. Stop-drop-roll smothers the fire by pressing it against the ground; a safety shower drenches it instantly. Either action stops the burn faster than removing the coat.",
  },
  {
    id: "sa-eye-splash",
    kind: "scenario-action",
    prompt: "Chemical splashes in your eye. First action?",
    choices: [
      "Go straight to the eyewash and rinse for 15 minutes, holding lids open",
      "Rub the eye to dislodge the chemical",
      "Pour bottled water from the bench into the eye",
      "Wait to see if vision blurs before acting",
    ],
    correctIndex: 0,
    explanation: "Use the eyewash station, hold eyelids open, rinse 15 minutes - every second counts.",
    whyCorrect:
      "Eyewash stations deliver a low-pressure flow designed to rinse without damaging the eye. Holding the lids open ensures the chemical doesn't pool under the eyelid. Even if it stings, the full 15-minute rinse is what prevents permanent damage.",
  },
  {
    id: "sa-mercury-spill",
    kind: "scenario-action",
    prompt: "A thermometer breaks and small mercury beads scatter. What do you do?",
    choices: [
      "Alert the teacher and evacuate the area - do not vacuum",
      "Vacuum the beads up immediately",
      "Sweep the beads into the trash",
      "Pick the beads up with bare fingers",
    ],
    correctIndex: 0,
    explanation: "Never vacuum mercury - it disperses toxic vapour. Use a mercury spill kit.",
    whyCorrect:
      "Mercury vapour is highly toxic and odourless. A standard vacuum aerosolises and spreads it throughout the room via the exhaust. Spill kits use a sulfur-based powder or special sponge to capture droplets safely.",
  },
  {
    id: "sa-flammable-fire",
    kind: "scenario-action",
    prompt: "Ethanol on the bench ignites. What's the right extinguisher?",
    choices: [
      "Smother with a fire blanket or use a CO₂ / dry-powder extinguisher",
      "Pour water on it",
      "Spray it with the eyewash hose",
      "Throw sand on it",
    ],
    correctIndex: 0,
    explanation: "Liquid fuel fires (Class B) require CO₂, dry powder, or a fire blanket - not water.",
    whyCorrect:
      "Water spreads burning ethanol because the fuel floats and flows on the water film. CO₂ and dry powder cut off oxygen without scattering the fuel. A fire blanket smothers small bench fires immediately.",
  },
  {
    id: "sa-gas-leak-smell",
    kind: "scenario-action",
    prompt: "You smell gas near a Bunsen burner that won't light. First action?",
    choices: [
      "Close the gas tap, ventilate, and tell the teacher - do not strike a flame",
      "Try a longer match - the gas will burn off",
      "Open the tap fully to flush the line",
      "Fan the air with your notebook to disperse it",
    ],
    correctIndex: 0,
    explanation: "Shut off the gas at the source first. No flames or sparks until the area is clear.",
    whyCorrect:
      "An accumulated gas-air mixture can flash or explode when ignited. Closing the valve stops the source; ventilation disperses what's there; only then is it safe to investigate. Striking a match in a leaking area can ignite the room.",
  },
  {
    id: "sa-cut-glass",
    kind: "scenario-action",
    prompt: "You cut your finger on broken glass. What do you do?",
    choices: [
      "Rinse the cut, apply pressure with a clean dressing, and notify the teacher",
      "Wrap a paper towel around it and continue",
      "Suck the wound to clean it",
      "Apply ethanol from the bench bottle",
    ],
    correctIndex: 0,
    explanation: "Rinse, apply pressure, report - every cut in a lab needs to be logged.",
    whyCorrect:
      "Rinsing flushes potential chemical contamination out of the wound. Pressure controls bleeding. Reporting is mandatory because broken glass + chemicals can cause delayed reactions and the incident must be recorded for follow-up.",
  },

  // ---------- ghs-pictogram ----------
  {
    id: "ghs-flame",
    kind: "ghs-pictogram",
    prompt: "Which hazard does this GHS pictogram warn about?",
    pictogram: "flame",
    choices: ["Flammable", "Oxidising", "Toxic", "Corrosive"],
    correctIndex: 0,
    explanation: "The flame symbol marks flammable gases, liquids, solids, and aerosols.",
    whyCorrect:
      "GHS uses a plain flame to indicate any substance that catches fire easily - including pyrophorics, self-heating materials, and substances that emit flammable gas on contact with water. Keep these away from heat, sparks, and open flames.",
  },
  {
    id: "ghs-exclamation",
    kind: "ghs-pictogram",
    prompt: "What does this GHS pictogram mean?",
    pictogram: "exclamation",
    choices: [
      "Irritant or harmful - moderate hazard",
      "Acutely toxic - fatal",
      "Long-term health hazard",
      "Environmental hazard",
    ],
    correctIndex: 0,
    explanation: "Exclamation mark = irritant, skin sensitiser, or moderate acute toxicity.",
    whyCorrect:
      "The exclamation pictogram covers harmful but non-life-threatening hazards: skin/eye irritation, narcotic effects, and respiratory irritation. It does NOT mean 'mildly dangerous to ignore' - full PPE is still required.",
  },
  {
    id: "ghs-skull",
    kind: "ghs-pictogram",
    prompt: "What does this GHS pictogram indicate?",
    pictogram: "skull",
    choices: [
      "Acute toxicity - can be fatal in small amounts",
      "Mild irritant",
      "Flammable",
      "Environmental hazard",
    ],
    correctIndex: 0,
    explanation: "Skull and crossbones = acutely toxic; even small exposures can be fatal.",
    whyCorrect:
      "This pictogram is reserved for category 1–3 acute toxicity by oral, dermal, or inhalation exposure. Substances bearing it must be handled in a fume hood with full PPE and a buddy nearby.",
  },
  {
    id: "ghs-corrosion",
    kind: "ghs-pictogram",
    prompt: "What does this GHS pictogram warn about?",
    pictogram: "corrosion",
    choices: [
      "Corrosive - destroys skin and metals",
      "Flammable liquid",
      "Compressed gas",
      "Reproductive toxin",
    ],
    correctIndex: 0,
    explanation: "Corrosion symbol = burns skin, damages eyes, and corrodes metals.",
    whyCorrect:
      "Two test tubes pouring onto a hand and a metal bar mark substances that cause severe skin burns and eye damage, or attack metals. Strong acids and bases (e.g. concentrated H₂SO₄, NaOH) carry this symbol.",
  },
  {
    id: "ghs-health-hazard",
    kind: "ghs-pictogram",
    prompt: "What does this GHS 'starburst on chest' pictogram mean?",
    pictogram: "health-hazard",
    choices: [
      "Serious long-term health hazard (carcinogen, mutagen, etc.)",
      "Acutely toxic",
      "Skin irritant",
      "Flammable solid",
    ],
    correctIndex: 0,
    explanation: "Health hazard = carcinogen, mutagen, reproductive toxin, or organ damage.",
    whyCorrect:
      "This pictogram covers chronic risks that may not appear immediately: cancer, reproductive harm, target-organ toxicity, respiratory sensitisation, and aspiration hazards. Long-term low-dose exposure is the danger.",
  },
  {
    id: "ghs-environment",
    kind: "ghs-pictogram",
    prompt: "What does this GHS pictogram indicate?",
    pictogram: "environment",
    choices: [
      "Hazardous to the aquatic environment",
      "Skin irritant",
      "Compressed gas",
      "Flammable solid",
    ],
    correctIndex: 0,
    explanation: "Dead fish and tree = harmful to aquatic life; never pour down the drain.",
    whyCorrect:
      "Substances marked with this pictogram cause acute or chronic harm to aquatic organisms. They must be collected as hazardous waste - never poured into a sink or storm drain, where they enter waterways.",
  },

  // ---------- ppe-id ----------
  {
    id: "ppe-acid-handling",
    kind: "ppe-id",
    prompt: "What PPE do you need to dispense concentrated H₂SO₄?",
    ppeIcon: "🧪",
    choices: [
      "Splash goggles, acid-resistant gloves, lab coat, fume hood",
      "Safety glasses and bare hands for grip",
      "Dust mask and latex gloves",
      "Lab coat only - fume hood is optional",
    ],
    correctIndex: 0,
    explanation: "Splash goggles + nitrile/neoprene gloves + lab coat, in a fume hood.",
    whyCorrect:
      "Concentrated sulfuric acid is corrosive and produces harmful mists. Splash goggles (not just safety glasses) seal around the eyes; nitrile or neoprene gloves resist acid better than latex; the fume hood removes mist. A lab coat protects clothing and skin.",
  },
  {
    id: "ppe-bunsen-burner",
    kind: "ppe-id",
    prompt: "Minimum PPE before lighting a Bunsen burner?",
    ppeIcon: "🥽",
    choices: [
      "Safety glasses, tied-back hair, no loose sleeves",
      "Just safety glasses - clothing doesn't matter",
      "Heat-resistant gloves and a face shield",
      "Respirator and ear protection",
    ],
    correctIndex: 0,
    explanation: "Glasses on, hair tied back, sleeves secured - minimum for any open flame.",
    whyCorrect:
      "Most Bunsen-related injuries are clothing or hair fires, not burns from the burner itself. The flame is nearly invisible at full air flow. Glasses protect from popping liquids or splashes; tied-back hair and secured sleeves prevent ignition.",
  },
  {
    id: "ppe-broken-glass",
    kind: "ppe-id",
    prompt: "Cleaning up broken glass - what should you wear/use?",
    ppeIcon: "🧤",
    choices: [
      "Cut-resistant gloves, dustpan and brush",
      "Latex gloves and bare hands",
      "Safety goggles only",
      "No PPE - glass is just sharp",
    ],
    correctIndex: 0,
    explanation: "Use cut-resistant gloves with a dustpan and brush - never bare hands.",
    whyCorrect:
      "Latex/nitrile gloves don't stop sharp shards; cut-resistant (Kevlar or HPPE) gloves do. A dustpan and brush keep your hands clear entirely. Pieces go in a rigid glass-only bin to protect cleaners later.",
  },
  {
    id: "ppe-bromine-water",
    kind: "ppe-id",
    prompt: "Working with bromine water - what's required?",
    ppeIcon: "🧥",
    choices: [
      "Goggles, nitrile gloves, lab coat, and a fume hood",
      "Safety glasses and latex gloves",
      "Respirator alone",
      "Goggles only - bromine water is dilute",
    ],
    correctIndex: 0,
    explanation: "Bromine vapour is toxic - use a fume hood with full splash PPE.",
    whyCorrect:
      "Even dilute bromine water releases bromine vapour, which is a strong respiratory irritant. The fume hood captures vapour; goggles and a lab coat handle splashes; nitrile gloves resist short-term contact better than latex.",
  },
];